import { FC, RefObject, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { Box } from "@mui/material";

import type { ITicketDetail } from "../../types/tickets";
import { linkedFilesFor } from "./ticketDisplay";
import TicketMessageCard from "./TicketMessageCard";

interface TicketsRendererProps {
  ticket: ITicketDetail;
  /** The scrollable element wrapping the articles, scrolled to the latest one */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const TicketsRenderer: FC<TicketsRendererProps> = observer(
  ({ ticket, scrollContainerRef }) => {
    const lastArticleRef = useRef<HTMLDivElement>(null);
    const articleCount = ticket.articles.length;

    // Once the ticket's articles render (and whenever a new one is appended,
    // e.g. after replying), scroll so the latest message card is in view.
    useEffect(() => {
      const container = scrollContainerRef.current;
      const lastArticle = lastArticleRef.current;
      if (!container || !lastArticle) return;
      container.scrollTop +=
        lastArticle.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
    }, [ticket.id, articleCount]);

    return (
      <>
        {ticket.articles.map((article, index) => (
          <Box
            key={article.id}
            ref={index === articleCount - 1 ? lastArticleRef : undefined}
          >
            <TicketMessageCard
              article={article}
              ticketId={ticket.id}
              linkedFiles={linkedFilesFor(ticket.files, article.id)}
              formatDateTime={formatDateTime}
            />
          </Box>
        ))}
      </>
    );
  },
);

export default TicketsRenderer;
